import { useEffect, useState } from "react";
import { supabase } from "./supabase.ts";

export function useFetchApartments() {
  const [apartments, setApartments] = useState([]);
  const [apartmentImages, setApartmentImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchApartments() {
      setLoading(true);
      try {
        // Fetch apartments from the database
        const { data: apartments, error } = await supabase.from("BellaTable").select("*");
        if (error) throw error;

        setApartments(apartments);

        // Fetch images for each apartment
        const apartmentImagesData = {};
        for (const apartment of apartments) {
          const apartmentName = apartment.ApartmentName;

          // Fetch images for the apartment
          const images = await fetchApartmentImages(apartmentName);
          apartmentImagesData[apartmentName] = images;
        }

        setApartmentImages(apartmentImagesData);
      } catch (error) {
        console.error("Error fetching apartments and images:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchApartments();
  }, []);

  // Utility function to fetch images for a specific apartment
  async function fetchApartmentImages(apartmentName) {
    try {
      // List images in the folder
      const { data: imagesData, error: imagesError } = await supabase
        .storage
        .from("Apartments")
        .list(apartmentName);
      if (imagesError) throw imagesError;

      // Get public URLs for the images
      const imageUrls = await Promise.all(
        imagesData.map(async (image) => {
          const { data, error } = supabase
            .storage
            .from("Apartments")
            .getPublicUrl(`${apartmentName}/${image.name}`);
          return error ? null : data.publicUrl;
        })
      );

      return imageUrls.filter((url) => url);
    } catch (error) {
      console.error(`Error fetching images for ${apartmentName}:`, error);
      return [];
    }
  }

  // Fetch data for a specific apartment dynamically
  async function fetchApartmentData(apartmentName) {
    try {
      setLoading(true);

      // Fetch the specific apartment from the database
      const { data: apartmentData, error } = await supabase
        .from("BellaTable")
        .select("*")
        .eq("ApartmentName", apartmentName)
        .single();
      if (error) throw error;

      // Fetch images for the specific apartment
      const images = await fetchApartmentImages(apartmentName);

      return { apartment: apartmentData, images };
    } catch (error) {
      console.error(`Error fetching data for ${apartmentName}:`, error);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return { apartments, apartmentImages, loading, fetchApartmentData };
}
